/**
 * The estimate's Now tab: the backend's rows (`EstimateDetailViewItem.now`)
 * laid out in the one Now pattern, each row's step as its button (the backend
 * composes them — see `views_module/api/transformers/estimate_now.py`).
 */
import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import type {
  NowAction,
  NowItem,
} from "@shared-core/data/order-to-invoice-types";
import { Button } from "../../app/components/primitives/button";
import { DropdownMenuItem } from "../../app/components/primitives/dropdown-menu";
import type { NowPanelItem } from "../../app/components/patterns/NowPanel";
import type { DraftEstimateBusyAction } from "../../app/components/domain/merchandise/DraftEstimateCostChangedBanner";

export interface EstimateNowSession {
  draftBusy: DraftEstimateBusyAction;
  onRepriceDraft: () => void;
  /** Open the withdraw confirmation. */
  onWithdrawDraft: () => void;
}

export function estimateNowItems(
  rows: NowItem[],
  session: EstimateNowSession,
): NowPanelItem[] {
  const items = rows.map((row, i) => serverItem(row, i, session));
  return [
    ...items.filter((item) => item.tone === "attention"),
    ...items.filter((item) => item.tone !== "attention"),
  ];
}

function serverItem(
  row: NowItem,
  index: number,
  session: EstimateNowSession,
): NowPanelItem {
  return {
    key: `${row.tone}-${index}`,
    tone: row.tone,
    lead: row.lead,
    consequence: row.consequence,
    when: row.when,
    info: row.info,
    isLate: row.is_late,
    action: row.action ? stepButton(row.action, session) : undefined,
    // The only ⋯ step an estimate row offers is withdrawing a draft.
    more: row.more.some((step) => step.kind === "withdraw_draft")
      ? row.more
          .filter((step) => step.kind === "withdraw_draft")
          .map((step) => (
            <DropdownMenuItem
              key={step.kind}
              disabled={session.draftBusy !== null}
              onSelect={session.onWithdrawDraft}
            >
              {step.label}
            </DropdownMenuItem>
          ))
      : undefined,
  };
}

function stepButton(step: NowAction, session: EstimateNowSession): ReactNode {
  if (step.kind !== "reprice_draft") return undefined;
  return (
    <Button
      size="sm"
      disabled={session.draftBusy !== null}
      onClick={session.onRepriceDraft}
    >
      {session.draftBusy === "reprice" && (
        <Loader2 className="size-3.5 animate-spin" />
      )}
      {step.label}
    </Button>
  );
}
